// SPDX-License-Identifier: Apache-2.0

// Right-rail chat panel. Lives next to VaultExperience.
// On send: classifies via Veil's routeMessage, then POSTs to /api/brain/chat
// (SSE). Renders streaming tokens + tool-call cards. Pops PermissionModal when
// a tool call needs approval.

import { For, Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { routeMessage, type Tier } from "@tether/veil";
import PermissionModal, { type PermissionRequest } from "./PermissionModal";
import SlackThreadCard from "./comms/SlackThreadCard";
import EmailPreviewCard from "./comms/EmailPreviewCard";
import { api } from "../lib/api";
import "../styles/chat.css";

type Role = "user" | "agent";

interface ToolCall {
  id: string;
  name: string;
  args: string;
  result?: string;
  status: "pending" | "approved" | "denied" | "done" | "error";
  expanded: boolean;
}

interface Message {
  id: string;
  role: Role;
  text: string;
  /** Set on user messages once the Veil router resolves. */
  tier?: Tier;
  /** Cohort-blended? Diagnostic from RouteResult. */
  blended?: boolean;
  toolCalls?: ToolCall[];
  /** Streaming agent message? */
  streaming?: boolean;
  /** Inline error rendered under the bubble. */
  error?: string;
  /** When true, render a "retry" button under the error. */
  recoverable?: boolean;
  /** The original user text that produced this turn — used by retry. */
  retryPayload?: string;
}

const BRAIN_ENDPOINT = "/api/brain/chat";
const LAST_MESSAGE_ENDPOINT = "/api/brain/last-message";

interface LostMessage {
  message: string;
  lostAt: string;
}

/**
 * Format a wall-clock ISO timestamp as a relative phrase ("just now",
 * "2 min ago", "1 hr ago", "2 days ago"). Used by the crash-resume banner.
 */
function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "moments ago";
  const deltaSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (deltaSec < 10) return "just now";
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} min ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr} hr ago`;
  const deltaDay = Math.round(deltaHr / 24);
  return `${deltaDay} day${deltaDay === 1 ? "" : "s"} ago`;
}

function previewMessage(s: string, n = 80): string {
  const trimmed = s.trim().replace(/\s+/g, " ");
  if (trimmed.length <= n) return trimmed;
  return trimmed.slice(0, n - 1) + "…";
}

const TIER_LABEL: Record<Tier, string> = {
  public: "public — direct",
  internal: "internal — direct",
  private: "private — Veil cohort-blended",
  secret: "secret — local-only",
};

const TIER_CLASS: Record<Tier, string> = {
  public: "is-public",
  internal: "is-internal",
  private: "is-private",
  secret: "is-secret",
};

const TOOLS_NEEDING_APPROVAL = new Set([
  "Bash",
  "create_document",
  "update_document",
  "delete_document",
  "rename_document",
  "merge_documents",
  "save_to_vault",
  "share_document",
]);

let nextId = 0;
const mkId = () => `m_${++nextId}_${Date.now().toString(36)}`;

export default function ChatPanel() {
  const [messages, setMessages] = createSignal<Message[]>([]);
  const [draft, setDraft] = createSignal("");
  const [permission, setPermission] = createSignal<PermissionRequest | null>(null);
  const [brainReachable, setBrainReachable] = createSignal<boolean | null>(null);
  const [collapsed, setCollapsed] = createSignal(false);
  // Asclepius — surface a "force release" link in the header after 30s of
  // streaming so the user can punch out of a stuck supervisor without
  // refreshing the page. Hidden during normal-length turns.
  const [streaming, setStreaming] = createSignal(false);
  const [showForceRelease, setShowForceRelease] = createSignal(false);
  // Crash-resume banner state. Set in two places:
  //   1. onMount: fetch /api/brain/last-message — covers the case where the
  //      user refreshed the page DURING a crash.
  //   2. {type:'agent_recovered'} SSE events — covers the live case.
  // Cleared on Resume (the resend itself supersedes) or Dismiss (server-side
  // POST to clear the saved message).
  const [lostMessage, setLostMessage] = createSignal<LostMessage | null>(null);
  let forceReleaseTimer: number | null = null;
  const pendingDecision = new Map<
    string,
    { resolve: (d: "allow" | "deny") => void }
  >();

  let listEl: HTMLDivElement | undefined;
  let textareaEl: HTMLTextAreaElement | undefined;
  let activeAbort: AbortController | null = null;

  const isEmpty = createMemo(() => messages().length === 0);

  // ---- ping the brain endpoint once on mount so we can show the empty state.
  const probeBrain = async () => {
    try {
      const res = await api(BRAIN_ENDPOINT, { method: "OPTIONS" });
      // Treat any response (including 405) as "endpoint exists" — only 503/network = unreachable.
      setBrainReachable(res.status !== 503);
    } catch {
      setBrainReachable(false);
    }
  };
  // Asclepius — query the supervisor for any message that was lost to a
  // mid-turn crash. If present, show the restoration banner. Quiet on the
  // happy path (null body → no banner, no UI noise).
  const fetchLastMessage = async () => {
    try {
      const res = await api(LAST_MESSAGE_ENDPOINT, { method: "GET" });
      if (!res.ok) return;
      const body = (await res.json()) as {
        message: string | null;
        lostAt: string | null;
      };
      if (body.message && body.lostAt) {
        setLostMessage({ message: body.message, lostAt: body.lostAt });
      }
    } catch {
      // Network blip — banner stays hidden, retry next mount.
    }
  };

  const dismissLostMessage = async () => {
    setLostMessage(null);
    try {
      await api(LAST_MESSAGE_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "dismiss" }),
      });
    } catch {
      // UI already cleared; server-side may still hold the message but the
      // next clean turn supersedes it. Safe to ignore here.
    }
  };

  const resumeLostMessage = () => {
    const lost = lostMessage();
    if (!lost) return;
    // Clear the banner BEFORE sending so the resend doesn't re-trigger it.
    setLostMessage(null);
    void send(lost.message);
  };

  onMount(() => {
    void probeBrain();
    void fetchLastMessage();
  });
  onCleanup(() => activeAbort?.abort());

  const scrollToEnd = () => {
    queueMicrotask(() => {
      if (!listEl) return;
      listEl.scrollTop = listEl.scrollHeight;
    });
  };

  const upsert = (id: string, patch: Partial<Message>) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    );
  };

  const askPermission = (
    name: string,
    args: string,
  ): Promise<"allow" | "deny"> => {
    return new Promise((resolve) => {
      const id = mkId();
      pendingDecision.set(id, { resolve });
      setPermission({ id, toolName: name, argsSummary: args });
    });
  };

  const handleDecision = (id: string, decision: "allow" | "deny") => {
    const pending = pendingDecision.get(id);
    pendingDecision.delete(id);
    setPermission(null);
    pending?.resolve(decision);
    // Wire to MCP server WS — caller layer plugs this in. Hook is the global
    // window.__tetherMcp?.respondPermission for now.
    const mcp = (globalThis as unknown as {
      __tetherMcp?: { respondPermission?: (id: string, d: string) => void };
    }).__tetherMcp;
    mcp?.respondPermission?.(id, decision);
  };

  // Begin a streaming watchdog that flips `showForceRelease` after 30s.
  const armForceReleaseWatchdog = () => {
    if (forceReleaseTimer != null) {
      clearTimeout(forceReleaseTimer);
      forceReleaseTimer = null;
    }
    setShowForceRelease(false);
    forceReleaseTimer = window.setTimeout(() => {
      if (streaming()) setShowForceRelease(true);
    }, 30_000);
  };
  const disarmForceReleaseWatchdog = () => {
    if (forceReleaseTimer != null) {
      clearTimeout(forceReleaseTimer);
      forceReleaseTimer = null;
    }
    setShowForceRelease(false);
  };

  // Force-release the supervisor's stuck slot. POSTs the recovery endpoint,
  // aborts the local stream, and clears local UI state so the next send
  // doesn't bounce. Best-effort: a network failure here still clears the UI.
  const forceRelease = async () => {
    try {
      await api("/api/brain/release", { method: "POST" });
    } catch {
      /* network blip — UI clear still happens below */
    }
    activeAbort?.abort();
    activeAbort = null;
    setStreaming(false);
    disarmForceReleaseWatchdog();
    // Mark any in-flight agent bubble as no longer streaming.
    setMessages((prev) =>
      prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)),
    );
  };

  const send = async (overrideText?: string) => {
    // The textarea is NOT disabled while streaming — typing is always allowed.
    // Explicit "send" cancels the previous turn (matches the original
    // activeAbort?.abort() pattern) and starts a new one.
    const text = (overrideText ?? draft()).trim();
    if (!text) return;
    if (overrideText === undefined) setDraft("");

    const userId = mkId();
    const agentId = mkId();
    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", text },
      {
        id: agentId,
        role: "agent",
        text: "",
        streaming: true,
        toolCalls: [],
        retryPayload: text,
      },
    ]);
    scrollToEnd();

    // ---- Veil checkpoint -------------------------------------------------
    try {
      const route = await routeMessage(text);
      upsert(userId, {
        tier: route.tier,
        blended: route.tier === "private" && !!route.cohortPlan,
      });
    } catch (err) {
      upsert(userId, { tier: "secret" });
      upsert(agentId, {
        text: `Veil routing failed: ${(err as Error).message}`,
        streaming: false,
      });
      return;
    }

    // ---- Stream from the brain agent ------------------------------------
    // If the user hits "send" while a previous turn is still running, cancel
    // the previous one — its server-side counterpart sees the SSE cancel and
    // SIGTERMs the subprocess via the supervisor's finally.
    activeAbort?.abort();
    activeAbort = new AbortController();
    setStreaming(true);
    armForceReleaseWatchdog();
    try {
      const res = await api(BRAIN_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "text/event-stream" },
        body: JSON.stringify({ message: text }),
        signal: activeAbort.signal,
      });
      if (res.status === 503 || !res.body) {
        setBrainReachable(false);
        upsert(agentId, { streaming: false });
        // Drop the empty agent shell on 503 — empty-state CTA covers the gap.
        setMessages((prev) => prev.filter((m) => m.id !== agentId));
        return;
      }
      setBrainReachable(true);
      await readSse(res.body, async (evt) => {
        if (evt.type === "token") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === agentId ? { ...m, text: m.text + evt.text } : m,
            ),
          );
          scrollToEnd();
        } else if (evt.type === "tool_use") {
          const tc: ToolCall = {
            id: evt.id,
            name: evt.name,
            args: truncate(evt.args, 240),
            status: TOOLS_NEEDING_APPROVAL.has(evt.name) ? "pending" : "approved",
            expanded: false,
          };
          setMessages((prev) =>
            prev.map((m) =>
              m.id === agentId
                ? { ...m, toolCalls: [...(m.toolCalls ?? []), tc] }
                : m,
            ),
          );
          if (TOOLS_NEEDING_APPROVAL.has(evt.name)) {
            const decision = await askPermission(evt.name, tc.args);
            patchTool(agentId, evt.id, {
              status: decision === "allow" ? "approved" : "denied",
            });
          }
        } else if (evt.type === "tool_result") {
          patchTool(agentId, evt.id, {
            result: evt.result,
            status: evt.error ? "error" : "done",
          });
        } else if (evt.type === "error") {
          // Asclepius: surface error inline. If `recoverable: true`, the
          // Show-block in the bubble renders a "retry" button.
          upsert(agentId, {
            error: evt.message,
            recoverable: !!evt.recoverable,
          });
          scrollToEnd();
        } else if (evt.type === "agent_recovered") {
          // Asclepius: the supervisor crashed mid-turn and auto-respawned.
          // The turn is definitively gone; show the restoration banner so
          // the user can choose to resend. We DON'T auto-replay — explicit
          // user consent only.
          setLostMessage({ message: evt.message, lostAt: evt.lostAt });
          // Mark the in-flight bubble as no-longer-streaming so the UI
          // doesn't keep pulsing forever. The banner sits above the list.
          upsert(agentId, { streaming: false });
          scrollToEnd();
        }
        // `done` events are end-of-stream markers; readSse will return.
      });
      upsert(agentId, { streaming: false });
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setBrainReachable(false);
      upsert(agentId, { streaming: false });
      setMessages((prev) => prev.filter((m) => m.id !== agentId));
    } finally {
      setStreaming(false);
      disarmForceReleaseWatchdog();
    }
  };

  const retryFrom = (m: Message) => {
    if (!m.retryPayload) return;
    // Drop the failed agent bubble + previous user echo so the resend creates
    // a clean pair. Match by retryPayload — if multiple bubbles share the
    // same payload (unlikely), we only drop the most recent.
    setMessages((prev) => {
      const lastIdx = prev.findIndex((x) => x.id === m.id);
      if (lastIdx === -1) return prev;
      // Drop this agent message AND the user message immediately preceding it.
      const drop = new Set<string>([m.id]);
      for (let i = lastIdx - 1; i >= 0; i--) {
        const candidate = prev[i];
        if (candidate && candidate.role === "user") {
          drop.add(candidate.id);
          break;
        }
      }
      return prev.filter((x) => !drop.has(x.id));
    });
    void send(m.retryPayload);
  };

  const patchTool = (agentId: string, toolId: string, patch: Partial<ToolCall>) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== agentId || !m.toolCalls) return m;
        return {
          ...m,
          toolCalls: m.toolCalls.map((t) =>
            t.id === toolId ? { ...t, ...patch } : t,
          ),
        };
      }),
    );
  };

  const toggleTool = (agentId: string, toolId: string) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== agentId || !m.toolCalls) return m;
        return {
          ...m,
          toolCalls: m.toolCalls.map((t) =>
            t.id === toolId ? { ...t, expanded: !t.expanded } : t,
          ),
        };
      }),
    );
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <>
      <aside
        class="mv-chat"
        classList={{ "mv-chat--collapsed": collapsed() }}
        aria-label="brain chat"
      >
        <header class="mv-chat__header">
          <div class="mv-chat__title">Brain</div>
          <Show when={streaming() && showForceRelease()}>
            <button
              type="button"
              class="mv-chat__force-release"
              onClick={() => void forceRelease()}
              title="The brain has been busy >30s. Click to clear the stuck slot."
            >
              force release
            </button>
          </Show>
          <button
            type="button"
            class="mv-chat__toggle"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed() ? "expand chat" : "collapse chat"}
            title={collapsed() ? "expand" : "collapse"}
          >
            {collapsed() ? "›" : "‹"}
          </button>
        </header>

        <Show when={lostMessage()}>
          {(lost) => (
            <div class="mv-chat__resume-banner" role="status">
              <div class="mv-chat__resume-text">
                Your last question (
                <span class="mv-chat__resume-preview">
                  {previewMessage(lost().message)}
                </span>
                ) was lost when the brain restarted{" "}
                <span class="mv-chat__resume-when">
                  {relativeTime(lost().lostAt)}
                </span>
                .
              </div>
              <div class="mv-chat__resume-actions">
                <button
                  type="button"
                  class="mv-chat__resume-btn mv-chat__resume-btn--primary"
                  onClick={() => resumeLostMessage()}
                >
                  Resume
                </button>
                <button
                  type="button"
                  class="mv-chat__resume-btn"
                  onClick={() => void dismissLostMessage()}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </Show>

        <div class="mv-chat__list" ref={listEl}>
          <Show
            when={!isEmpty()}
            fallback={
              <Show
                when={brainReachable() === false}
                fallback={
                  <div class="mv-chat__hint">
                    <p>Ask anything. Veil routes private things on-device.</p>
                  </div>
                }
              >
                <div class="mv-chat__empty">
                  <div class="mv-chat__empty-eyebrow">brain offline</div>
                  <div class="mv-chat__empty-line">
                    Brain not running locally — run{" "}
                    <code>curl tether.dev/setup | bash</code>
                  </div>
                  <button
                    type="button"
                    class="mv-chat__retry"
                    onClick={() => void probeBrain()}
                  >
                    Retry
                  </button>
                </div>
              </Show>
            }
          >
            <For each={messages()}>
              {(m) => (
                <div class={`mv-msg mv-msg--${m.role}`}>
                  <Show when={m.role === "user" && m.tier && m.tier !== "public"}>
                    <span class={`mv-tier ${TIER_CLASS[m.tier!]}`} title={TIER_LABEL[m.tier!]}>
                      {m.tier}
                      {m.blended ? " · blended" : ""}
                    </span>
                  </Show>
                  <div class="mv-msg__bubble">
                    <Show when={m.text}>
                      <div class="mv-msg__text">{m.text}</div>
                    </Show>
                    <Show when={m.streaming && !m.text}>
                      <div class="mv-msg__pulse" aria-label="thinking" />
                    </Show>
                    <Show when={m.toolCalls && m.toolCalls.length > 0}>
                      <div class="mv-tools">
                        <For each={m.toolCalls}>
                          {(t) => (
                            <div class={`mv-tool mv-tool--${t.status}`}>
                              <button
                                type="button"
                                class="mv-tool__head"
                                onClick={() => toggleTool(m.id, t.id)}
                              >
                                <span class="mv-tool__name">{t.name}</span>
                                <span class="mv-tool__args">{t.args}</span>
                                <span class="mv-tool__status">{t.status}</span>
                              </button>
                              <Show when={t.expanded && t.result !== undefined}>
                                {/* Iris — specialised comms cards. The switch is
                                  PROGRESSIVE-ENHANCEMENT only: if the result shape
                                  doesn't match the card's expected schema, the card
                                  itself falls back to a "no messages" placeholder.
                                  Generic JSON expander stays for everything else. */}
                                {t.name.startsWith("slack.") ? (
                                  <SlackThreadCard
                                    result={t.result}
                                    toolName={t.name}
                                  />
                                ) : t.name.startsWith("mail.") ? (
                                  <EmailPreviewCard
                                    result={t.result}
                                    toolName={t.name}
                                  />
                                ) : (
                                  <pre class="mv-tool__result">{t.result}</pre>
                                )}
                              </Show>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                    <Show when={m.error}>
                      <div class="mv-msg__error">
                        <span class="mv-msg__error-text">{m.error}</span>
                        <Show when={m.recoverable}>
                          <button
                            type="button"
                            class="mv-msg__retry"
                            onClick={() => retryFrom(m)}
                          >
                            retry
                          </button>
                        </Show>
                      </div>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </Show>
        </div>

        <footer class="mv-chat__footer">
          <textarea
            ref={textareaEl}
            class="mv-chat__input"
            placeholder="Ask the brain. Cmd+Enter to send."
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={onKey}
            rows={2}
          />
          <div class="mv-chat__footer-row">
            <Show when={import.meta.env.DEV}>
              <button
                type="button"
                class="mv-chat__dev"
                onClick={() =>
                  setPermission({
                    id: mkId(),
                    toolName: "Bash",
                    argsSummary: "ls ~/brain",
                  })
                }
                title="dev only — fake a permission prompt"
              >
                dev: perm
              </button>
            </Show>
            <button
              type="button"
              class="mv-chat__send"
              onClick={() => void send()}
              disabled={!draft().trim()}
            >
              Send
            </button>
          </div>
        </footer>
      </aside>

      <PermissionModal request={permission()} onDecide={handleDecision} />
    </>
  );
}

// ---- helpers ---------------------------------------------------------------

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

type SseEvent =
  | { type: "tier"; tier: Tier; escalated: boolean }
  | { type: "token"; text: string }
  | { type: "tool_use"; id: string; name: string; args: string }
  | { type: "tool_result"; id: string; result: string; error?: boolean }
  | { type: "error"; message: string; recoverable?: boolean }
  | { type: "agent_recovered"; message: string; lostAt: string }
  | { type: "done"; reason: string };

async function readSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (e: SseEvent) => Promise<void> | void,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const data = block
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim())
        .join("\n");
      if (!data) continue;
      try {
        const parsed = JSON.parse(data) as SseEvent;
        await onEvent(parsed);
      } catch {
        // tolerate malformed events; brain agent is upstream-of-record.
      }
    }
  }
}
